import type { FC, SyntheticEvent } from "react";
import { useIntl } from "react-intl";
import styled from "styled-components";
import "@nectary/components/tooltip";
import { NectaryIcon } from "../Icon";
import { focusVisible } from "./SidebarItemElements";
import { useSidebarDispatch, useSidebarState } from "./state";

const Button = styled.button<{
  "aria-label": string;
}>`
  right: 0;
  top: 52px;
  transform: translateX(50%);
  fill: var(--sinch-sys-color-container-contrast-primary-foreground);
  background-color: var(--sinch-sys-color-container-main-default);
  border: 1px solid;
  border-color: var(--sinch-sys-color-container-contrast-primary-active);

  &:hover {
    --sinch-global-color-icon: var(--sinch-sys-color-primary-foreground);

    background-color: var(
      --sinch-sys-color-container-contrast-primary-foreground
    );
    border-color: var(--sinch-sys-color-container-contrast-primary-foreground);
  }

  transition-property: fill, background-color, border-color;
  transition-duration: 0.3ms;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  outline: none;

  ${focusVisible("-6px", "-6px", "absolute")}
`;

const IconContainer = styled.span<{
  isCollapsed: boolean;
}>`
  transform: ${({ isCollapsed }) => (isCollapsed ? "rotate(180deg)" : "none")};
  transition: transform 0.5s;
`;

const HitBox = styled.div`
  position: absolute;
  top: -8px;
  left: -3px;
  display: block;
  width: 40px;
  height: 40px;
  background-color: transparent;
`;

const stopPropagation = (e: SyntheticEvent) => e.stopPropagation();

export const CollapseButton: FC = () => {
  const intl = useIntl();
  const state = useSidebarState();
  const dispatch = useSidebarDispatch();

  const tooltipText = intl.formatMessage({
    id: state.isCollapsed
      ? "menuCollapseButtonExpand"
      : "menuCollapseButtonCollapse",
  });

  return (
    <Button
      // Cancel the onPointerOver event so that the menu stays in its current
      // state when hovering over the button.
      onPointerOver={stopPropagation}
      // Since we trigger hovering when menu is focused we need to cancel that
      // event from reaching the menu, otherwise there is a race condition with
      // the click event happening fast enough before the button moves out from
      // under the cursor.
      onFocus={stopPropagation}
      aria-label={tooltipText}
      onClick={() =>
        dispatch({ type: state.isCollapsed ? "EXPAND" : "COLLAPSE" })
      }
    >
      <sinch-tooltip text={tooltipText} orientation={"right"}>
        <IconContainer isCollapsed={state.isCollapsed}>
          <NectaryIcon name="keyboard-arrow-left" />
        </IconContainer>
        <HitBox />
      </sinch-tooltip>
    </Button>
  );
};
